import { useTranslation } from 'react-i18next';

import { GENERAL_SETTINGS_PERMISSION } from '@/console/features/general-settings/general-settings.permissions';
import { BillingIcon } from '@/shared/assets/icons/billing-icon';
import { DevicesIcon } from '@/shared/assets/icons/devices-icon';
import { DocumentsIcon } from '@/shared/assets/icons/documents-icon';
import { FolderIcon } from '@/shared/assets/icons/folder-icon';
import { GatewayIcon } from '@/shared/assets/icons/gateway-icon';
import { GeneralSettingsIcon } from '@/shared/assets/icons/general-settings-icon';
import { HomeIcon } from '@/shared/assets/icons/home-icon';
import { IdeaasIcon } from '@/shared/assets/icons/ideaas-icon';
import { LicenseIcon } from '@/shared/assets/icons/license-icon';
import { MapIcon } from '@/shared/assets/icons/map-icon';
import { PipelinesIcon } from '@/shared/assets/icons/pipelines-icon';
import { ProjectsIcon } from '@/shared/assets/icons/projects-icon';
import { SecurityIcon } from '@/shared/assets/icons/security-icon';
import { UserIcon } from '@/shared/assets/icons/user-icon';
import { UserRoleIcon } from '@/shared/assets/icons/user-role-icon';
import { WorkstationIcon } from '@/shared/assets/icons/workstation-icon';
import { BlinkingIndicator } from '@/shared/components/ui/blinking-indicator/blinking-indicator';
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
} from '@/shared/components/ui/pagination/hooks/use-pagination-query-params/use-pagination-query-params';
import { TPermissionAction } from '@/shared/features/permissions/actions';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import { TNavigationSection } from '@/shared/features/sidebar/sidebar.types';
import { useFeatureFlag } from '@/shared/hooks/use-feature-flag/use-feature-flag';
import {
  FEATURE_IDEAAS_HUB,
  FEATURE_MAP_OVERVIEW,
  FEATURE_SECURE_REMOTE_ACCESS,
} from '@/shared/hooks/use-feature-flag/use-feature-flag.constants';
import { useVulnerabilityWarning } from '@/shared/hooks/use-vulnerability-warning/use-vulnerability-warning';
import { useWeakSecretWarning } from '@/shared/hooks/use-weak-secret-warning/use-weak-secret-warning';
import { devicesApi } from '@/shared/redux/api/assets/devices/devices-api';
import { gatewaysApi } from '@/shared/redux/api/assets/gateways/gateways-api';
import { projectsApi } from '@/shared/redux/api/assets/projects/projects-api';
import { jobsApi } from '@/shared/redux/api/jobs/jobs-api';

const SECURITY_HUB_READ_ACTIONS: TPermissionAction[] = [
  'vault:readVault',
  'auditLog:readAuditLog',
  'vulnerabilities:readVulnerabilities',
];

export const useConsoleSidebarSections = (): TNavigationSection[] => {
  const { t } = useTranslation();
  const { canAny } = usePermissionCheck();
  const hasPermissionToSeeHubs = canAny(SECURITY_HUB_READ_ACTIONS);
  const _prefetchProjects = projectsApi.usePrefetch('ReadAllProjects');
  const _prefetchDevices = devicesApi.usePrefetch('ReadAllDevices');
  const _prefetchPipelines = jobsApi.usePrefetch('ReadAllPipelines');
  const _prefetchGateways = gatewaysApi.usePrefetch('ReadAllGateways');
  const { affectedDevicesCount } = useVulnerabilityWarning();
  const { weakSecretsCount } = useWeakSecretWarning();

  const hasSecurityHubWarning =
    affectedDevicesCount > 0 || weakSecretsCount > 0;

  const { isFeatureFlagEnabled } = useFeatureFlag();

  const pagination = {
    page: Number(DEFAULT_PAGE),
    pageSize: Number(DEFAULT_PAGE_SIZE),
  };

  const prefetchProjects = () =>
    _prefetchProjects({
      pagination,
    });

  const prefetchDevices = () => _prefetchDevices({ pagination });
  const prefetchPipelines = () => _prefetchPipelines();
  const prefetchGateways = () => _prefetchGateways({ pagination });

  const getHubsSection = (): TNavigationSection | undefined => {
    if (!hasPermissionToSeeHubs) {
      return;
    }

    return {
      name: t('ui:hubs'),
      children: [
        {
          name: t('pages:ideaas-hub'),
          icon: <IdeaasIcon />,
          link: '/ideaas-hub',
          hide: !isFeatureFlagEnabled(FEATURE_IDEAAS_HUB),
        },
        {
          name: t('pages:security-hub'),
          icon: (
            <BlinkingIndicator visible={hasSecurityHubWarning}>
              <SecurityIcon width={20} height={20} />
            </BlinkingIndicator>
          ),
          link: '/security-hub',
          permission: {
            action: SECURITY_HUB_READ_ACTIONS,
            any: true,
          },
        },
      ],
    };
  };

  const hubsSection = getHubsSection();

  const mainSection: TNavigationSection = {
    children: [
      {
        name: t('pages:home'),
        icon: <HomeIcon />,
        link: '/',
      },
      {
        name: t('pages:groups'),
        icon: <FolderIcon />,
        link: '/groups',
        permission: {
          action: 'resourceGroup:readResourceGroup',
        },
      },
      {
        name: t('pages:gateways'),
        icon: <GatewayIcon />,
        link: '/gateways',
        prefetchApi: prefetchGateways,
        permission: {
          action: 'gateway:readGateway',
        },
      },
      {
        name: t('pages:devices'),
        icon: <DevicesIcon />,
        link: '/devices',
        prefetchApi: prefetchDevices,
        permission: {
          action: 'device:readDevice',
        },
      },
      {
        name: t('pages:projects'),
        icon: <ProjectsIcon />,
        link: '/projects',
        prefetchApi: prefetchProjects,
        permission: {
          action: 'project:readProject',
        },
      },
      {
        name: t('pages:documents'),
        icon: <DocumentsIcon />,
        link: '/documents',
        permission: {
          action: 'document:readDocument',
        },
      },
      {
        name: t('pages:pipelines'),
        icon: <PipelinesIcon />,
        link: '/pipelines',
        prefetchApi: prefetchPipelines,
        permission: {
          action: 'pipeline:readPipeline',
        },
      },
      {
        name: t('pages:map'),
        icon: <MapIcon />,
        link: '/map',
        hide: !isFeatureFlagEnabled(FEATURE_MAP_OVERVIEW),
        permission: {
          action: 'gateway:readGateway',
        },
      },
    ],
  };

  const adminSection: TNavigationSection = {
    name: t('pages:admin'),
    children: [
      {
        name: t('pages:general-settings'),
        icon: <GeneralSettingsIcon />,
        link: '/general-settings',
        permission: GENERAL_SETTINGS_PERMISSION,
      },
      {
        name: t('pages:users'),
        icon: <UserIcon />,
        link: '/users',
        permission: {
          action: 'ident:readUser',
        },
      },
      {
        name: t('pages:roles'),
        icon: <UserRoleIcon />,
        link: '/roles',
        permission: {
          action: 'ident:readUserRole',
        },
      },
      {
        name: t('pages:workstations'),
        icon: <WorkstationIcon />,
        link: '/sra/workstations',
        hide: !isFeatureFlagEnabled(FEATURE_SECURE_REMOTE_ACCESS),
      },
      {
        name: t('pages:plans-and-billing'),
        icon: <BillingIcon />,
        link: '/plans-and-billing',
        disabled: true,
        hide: true,
      },
      {
        name: t('pages:license-management'),
        icon: <LicenseIcon />,
        link: '/license-management',
        permission: {
          action: 'license:readLicense',
        },
      },
    ],
  };

  const allSections = [mainSection, hubsSection, adminSection];

  return allSections.flatMap((section) => {
    if (!section || section.hide) {
      return [];
    }

    return [section];
  });
};
